import { FC } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  ContentPanel,
  DataTable,
  InfiniteScroll,
  ProfileImage,
  ProfileInitials,
  SimpleLabel,
  Text,
} from '@hopskipdrive/react-components';
import { ActionBar, SearchNavBar } from '@rideiq/shared/components';
import { PageHeading } from '@rideiq/shared/components/PageHeading';

import { useNavigation } from '../../../shared/hooks/useNavigation';
import { LocationDetailsHeader } from '../../components/LocationDetailsLayout';
import { useLocationQuery } from '../../hooks/useLocationQuery';

import { useRiders } from './useRiders';

const RiderInstructions: FC = () => {
  const { locationId } = useParams() as { locationId: string };
  const { locationName, riderCount } = useLocationQuery(locationId);
  const { riders, handlePagination, debounceHandleQuery, queryParams, isLoading } = useRiders();
  const { navigateTo } = useNavigation();

  return (
    <>
      <LocationDetailsHeader riderCount={riderCount}>
        <ActionBar.Breadcrumbs
          crumbs={[
            {
              title: 'All Locations',
              pathname: '/locations',
            },
            {
              title: locationName,
              pathname: `${locationId}`,
            },
          ]}
        />
        <ActionBar.Title title="Rider Instructions" />
      </LocationDetailsHeader>
      <ContentPanel>
        <Box>
          <Box display="flex" alignItems="center" mb={5}>
            <PageHeading mr="auto" variant="h2">
              Riders
            </PageHeading>
            <SearchNavBar
              debounceHandleQuery={debounceHandleQuery}
              placeholder="Search rider by name"
              query={queryParams.query}
            />
          </Box>
          <InfiniteScroll
            itemCount={riders.length}
            pageNumber={queryParams.page}
            perPage={queryParams.perPage}
            handleNextPage={handlePagination}
          >
            <DataTable
              columns={[
                {
                  align: 'left',
                  valueGetter: ({ fullName, profilePhotoUrl, instructions }) => (
                    <Box display="flex" alignItems="center">
                      {profilePhotoUrl && profilePhotoUrl.indexOf('default_photo') === -1 ? (
                        <ProfileImage key={fullName} imgSrc={profilePhotoUrl} />
                      ) : (
                        <ProfileInitials key={fullName} fullName={fullName} />
                      )}
                      <Text ml={7}>{fullName}</Text>
                      {instructions && <SimpleLabel ml={7} label="Instructions" />}
                    </Box>
                  ),
                },
                {
                  key: 'numUpcomingRides',
                  align: 'right',
                  valueSetter: (cellValue) => `${cellValue} upcoming rides`,
                },
              ]}
              rows={riders}
              handleRowClick={({ slug }) => navigateTo(`/locations/${locationId}/rider_instructions/${slug}`)}
              href={({ slug }) => `/locations/${locationId}/rider_instructions/${slug}`}
              isLoading={isLoading}
            />
          </InfiniteScroll>
        </Box>
      </ContentPanel>
    </>
  );
};

export default RiderInstructions;
